<script setup>
/**
 * Create or edit a one-off WhatsApp campaign.
 *
 * A campaign leaves this form in one of two shapes: a `draft`, which is saved
 * but never sent, or an `active` one carrying the time it should go out. The
 * status is part of the submitted payload — the backend publishes a draft on the
 * `draft -> active` transition, so scheduling *is* the publish action.
 *
 * A gallery pick arrives as `starterTemplate`. WhatsApp only delivers
 * Meta-approved message templates, so a starting point cannot supply the
 * message: it prefills the title and shows its suggested copy beside the
 * template picker, to match against an approved template or submit as a new one.
 */
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredUnless, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import CampaignFormField from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignFormField.vue';
import { CAMPAIGN_SELECT_CLASS } from 'dashboard/components-next/Campaigns/helpers/formFieldClasses';
import { CAMPAIGN_STATUS } from 'dashboard/components-next/Campaigns/helpers/campaignStatus';
import { buildWhatsAppStarter } from 'dashboard/components-next/Campaigns/helpers/campaignGallery';
import {
  buildAudienceOptions,
  buildAudiencePayload,
  buildAudienceValues,
} from 'dashboard/components-next/Campaigns/helpers/campaignAudience';

const props = defineProps({
  // The campaign to edit; null when creating a new one.
  campaign: {
    type: Object,
    default: null,
  },
  // The gallery template this campaign starts from; null for a blank one.
  starterTemplate: {
    type: Object,
    default: null,
  },
});
const emit = defineEmits(['submit', 'cancel']);
// The fallbacks a campaign's `template_params` are written with, so a saved
// campaign can be matched back to the template it was built from.
const DEFAULT_TEMPLATE_LANGUAGE = 'en_US';
const DEFAULT_TEMPLATE_CATEGORY = 'UTILITY';

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  segments: useMapGetter('customViews/getContactCustomViews'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

// `scheduled_at` is serialized as epoch seconds; the input wants a local
// `YYYY-MM-DDTHH:mm`. An unscheduled campaign serializes as 0.
const toDateTimeLocal = epochSeconds => {
  if (!epochSeconds) return null;
  const scheduled = new Date(epochSeconds * 1000);
  return new Date(scheduled.getTime() - scheduled.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

// A gallery pick only prefills what WhatsApp lets it: the campaign title.
const starter = computed(() =>
  props.starterTemplate ? buildWhatsAppStarter(props.starterTemplate) : null
);

const buildInitialState = (campaign, starterTitle = '') => ({
  title: campaign?.title ?? starterTitle,
  inboxId: campaign?.inbox?.id ?? null,
  // Resolved from `template_params` once the inbox's templates have loaded.
  templateId: null,
  scheduledAt: toDateTimeLocal(campaign?.scheduled_at),
  selectedAudience: buildAudienceValues(campaign?.audience ?? []),
});

const state = reactive(
  buildInitialState(props.campaign, starter.value?.title ?? '')
);
const templateParserRef = ref(null);
// True only while a draft save is in flight, which is what relaxes the
// scheduled-time rule below.
const isSavingDraft = ref(false);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  // A draft is unscheduled by definition; the time is only required to publish.
  scheduledAt: { required: requiredUnless(isSavingDraft) },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isEditing = computed(() => Boolean(props.campaign));

// Only a draft can be saved as a draft again: the backend allows
// `draft -> active` and never the way back.
const canSaveAsDraft = computed(
  () =>
    !isEditing.value || props.campaign.campaign_status === CAMPAIGN_STATUS.DRAFT
);

const isSubmitting = computed(
  () => formState.uiFlags.value.isCreating || formState.uiFlags.value.isUpdating
);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  buildAudienceOptions({
    labels: formState.labels.value,
    segments: formState.segments.value,
    formatLabel: name =>
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL_OPTION', { name }),
    formatSegment: name =>
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.SEGMENT_OPTION', { name }),
  })
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const savedTemplateParams = computed(() => props.campaign?.template_params);
// The id the campaign's saved template resolved to, so a later change of
// template is not re-seeded with the previous one's values.
const savedTemplateId = ref(null);

const prefilledTemplateParams = computed(() =>
  state.templateId && state.templateId === savedTemplateId.value
    ? (savedTemplateParams.value?.processed_params ?? null)
    : null
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

// Everything a draft needs; the scheduled time is the only field it may omit.
const DRAFT_FIELDS = ['title', 'inboxId', 'templateId', 'selectedAudience'];

const isDraftSubmitDisabled = computed(
  () =>
    DRAFT_FIELDS.some(field => v$.value[field].$invalid) ||
    !hasRequiredTemplateParams.value
);

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

// Scheduling an existing campaign that is already live is just a save; for a new
// campaign or a draft it is the publish action.
const submitLabel = computed(() =>
  canSaveAsDraft.value
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.SCHEDULE')
    : t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.SAVE')
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, buildInitialState(null));
  savedTemplateId.value = null;
  isSavingDraft.value = false;
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

// The suggested copy is what the agent pastes into Meta's template submission,
// so copying it is the one action the starter panel needs.
const handleCopySuggestedBody = async () => {
  await copyTextToClipboard(starter.value.suggestedBody);
  useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.STARTER.COPIED'));
};

const prepareCampaignDetails = asDraft => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || DEFAULT_TEMPLATE_CATEGORY,
    language: currentTemplate?.language || DEFAULT_TEMPLATE_LANGUAGE,
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: buildAudiencePayload(state.selectedAudience),
    campaign_status: asDraft ? CAMPAIGN_STATUS.DRAFT : CAMPAIGN_STATUS.ACTIVE,
  };
};

const submit = async asDraft => {
  isSavingDraft.value = asDraft;

  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    isSavingDraft.value = false;
    return;
  }

  emit('submit', prepareCampaignDetails(asDraft));
  resetState();
  handleCancel();
};

const handleSubmit = () => submit(false);
const handleSaveAsDraft = () => submit(true);

// The campaign stores its template by name and language rather than by id, and
// the inbox's templates arrive with the inbox list — so the selection is
// resolved the first time they are available. The same watcher drops a
// selection the current inbox no longer offers.
watch(
  templateOptions,
  options => {
    const saved = savedTemplateParams.value;

    if (saved && savedTemplateId.value === null) {
      const match = options.find(
        option =>
          option.template.name === saved.name &&
          (option.template.language || DEFAULT_TEMPLATE_LANGUAGE) ===
            saved.language
      );

      if (match) {
        savedTemplateId.value = match.value;
        state.templateId = match.value;
        return;
      }
    }

    if (
      state.templateId &&
      !options.some(option => option.value === state.templateId)
    ) {
      state.templateId = null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <div
      v-if="starter"
      data-testid="whatsapp-starter"
      class="flex flex-col gap-2 p-3 rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <div class="flex items-start justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.STARTER.TITLE') }}
        </span>
        <Button
          variant="link"
          color="slate"
          size="xs"
          icon="i-lucide-copy"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STARTER.COPY')"
          type="button"
          @click="handleCopySuggestedBody"
        />
      </div>
      <p
        data-testid="whatsapp-starter-body"
        class="mb-0 text-sm whitespace-pre-line text-n-slate-12"
      >
        {{ starter.suggestedBody }}
      </p>
      <p class="mb-0 text-label-small text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.STARTER.HINT') }}
      </p>
    </div>

    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <CampaignFormField
      field-id="inbox"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL')"
    >
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        :class="CAMPAIGN_SELECT_CLASS"
      />
    </CampaignFormField>

    <CampaignFormField
      field-id="template"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL')"
    >
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        :class="CAMPAIGN_SELECT_CLASS"
      />
    </CampaignFormField>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
      :initial-params="prefilledTemplateParams"
    />

    <CampaignFormField
      field-id="audience"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
    >
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
      />
    </CampaignFormField>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="flex-1 bg-n-alpha-2 text-n-violet-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        v-if="canSaveAsDraft"
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.SAVE_DRAFT')"
        class="flex-1"
        :is-loading="isSubmitting"
        :disabled="isSubmitting || isDraftSubmitDisabled"
        @click="handleSaveAsDraft"
      />
      <Button
        :label="submitLabel"
        class="flex-1"
        type="submit"
        :is-loading="isSubmitting"
        :disabled="isSubmitting || isSubmitDisabled"
      />
    </div>
  </form>
</template>
